import React, { useEffect, useState } from "react";
import TeamHeader from "../TeamHeader/TeamHeader";
import "./DataManagement.css";

// Icons
import { IoMdArrowDropup, IoMdArrowDropdown } from "react-icons/io";
import { HiPencilSquare } from "react-icons/hi2";
import { BiSortAlt2 } from "react-icons/bi";
import { IoOptionsOutline } from "react-icons/io5";
import { PiGearSix } from "react-icons/pi";

// Toast imports
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Helper
import { usePrefectHistory, PrefectPipelineData } from "./prefect";

interface MarketingOrg {
    id: number;
    ORG_NAME: string;
    ORG_LOGO: string;
    ORG_PLAN: string;
}

type SortOption = "default" | "alphabetical" | "runtime" | "last" | "active_since";

const DataManagement = () => {
    const { pipelineHistory, emailHistory, keyHistory } = usePrefectHistory();
    const [orgs, setOrgs] = useState<MarketingOrg[]>([]);

    // Independent sort/filter state per block
    const [pipelineSort, setPipelineSort] = useState<SortOption>("last");
    const [pipelineFilter, setPipelineFilter] = useState<{ area?: string; status?: string }>({});

    const [emailSort, setEmailSort] = useState<SortOption>("last");
    const [emailFilter, setEmailFilter] = useState<{ area?: string; status?: string; customer?: string }>({});

    const [keySort, setKeySort] = useState<SortOption>("last");
    const [keyFilter, setKeyFilter] = useState<{ area?: string; active?: string; customer?: string }>({});

    // Scroll to top on page opening
    useEffect(() => window.scrollTo(0, 0), []);

    // Helper: truncate URL
    const shortUrl = (url: string) =>
        url.length > 25 ? `${url.slice(0, 22)}...` : url;

    const fetchOrgs = async () => {
        try {
            const response = await fetch("/portal/api/marketing_orgs/", {
                credentials: "same-origin",
            });
            const data = await response.json();
            if (response.ok) {
                setOrgs(data);
            } else {
                console.error("Failed to load orgs:", data.error || response.statusText);
            }
        } catch (error) {
            console.error("❌ Error fetching orgs:", error);
        }
    };

    useEffect(() => {
        fetchOrgs();
    }, []);

    /* ----------------------------------------------------
        SORT + FILTER HELPERS
    ---------------------------------------------------- */

    const sortByOption = (data: any[], sort: SortOption, field: string) => {
        if (sort === "alphabetical") {
            return [...data].sort((a, b) => a.area.localeCompare(b.area));
        }
        if (sort === "runtime") {
            return [...data].sort((a, b) => a.runtime.localeCompare(b.runtime));
        }
        if (sort === "last") {
            return [...data].sort((a, b) => new Date(b[field]).getTime() - new Date(a[field]).getTime());
        }
        if (sort === "active_since") {
            return [...data].sort((a, b) => a.active_since.localeCompare(b.active_since));
        }
        return data; // default backend order
    };

    const toggleFilter = (setStateFn: any, key: string, value: string) => {
        setStateFn((prev: any) => ({
            ...prev,
            [key]: prev[key] === value ? undefined : value,
        }));
    };

    // Helper function to get color based on value
    const getStatusColor = (status: boolean | string, type: "status" | "active") => {
        if (type === "status") {
            return status === true || status === "Successful" ? "#28C76F" : "#E63946";
        }
        if (type === "active") {
            return status === true || status === "Yes" ? "#28C76F" : "#E63946";
        }
        return "#000"; // fallback
    };


    /* ----------------------------------------------------
        PIPELINE — APPLYING FILTERS & SORTING
    ---------------------------------------------------- */
    const filteredPipeline = pipelineHistory
        .filter((p) => (!pipelineFilter.area || p.area === pipelineFilter.area))
        .filter((p) => {
            if (!pipelineFilter.status) return true;
            return pipelineFilter.status === "Successful" ? p.status === true : p.status === false;
        });

    const sortedPipeline = sortByOption(filteredPipeline, pipelineSort, "timestamp");

    /* ----------------------------------------------------
        EMAIL — APPLYING FILTERS & SORTING
    ---------------------------------------------------- */
    const filteredEmail = emailHistory
        .filter((e) => (!emailFilter.area || e.area === emailFilter.area))
        .filter((e) => {
            if (!emailFilter.customer) return true;
            const name = orgs.find((o) => o.id === e.organization)?.ORG_NAME;
            return name === emailFilter.customer;
        })
        .filter((e) => {
            if (!emailFilter.status) return true;
            return emailFilter.status === "Successful" ? e.status === true : e.status === false;
        });

    const sortedEmail = sortByOption(filteredEmail, emailSort, "email_timestamp");

    /* ----------------------------------------------------
        KEY — APPLYING FILTERS & SORTING
    ---------------------------------------------------- */
    const filteredKey = keyHistory
        .filter((k) => (!keyFilter.area || k.area === keyFilter.area))
        .filter((k) => {
            if (!keyFilter.customer) return true;
            const name = orgs.find((o) => o.id === k.organization)?.ORG_NAME;
            return name === keyFilter.customer;
        })
        .filter((k) => {
            if (!keyFilter.active) return true;
            return keyFilter.active === "Yes" ? k.active === true : k.active === false;
        });

    const sortedKey = sortByOption(filteredKey, keySort, "last_used");


    /* ----------------------------------------------------
        DROPDOWN LOGIC
    ---------------------------------------------------- */

    interface DropdownProps {
        title: string;
        options: string[];
        selected?: string;
        onSelect: (value: string) => void;
        open: boolean;
        setOpen: (val: boolean) => void;
    }

    const Dropdown: React.FC<DropdownProps> = ({ title, options, selected, onSelect, open, setOpen }) => {
        return (
            <div className="general-dropdown">
                <div className="general-dropdown-header" onClick={() => setOpen(!open)}>
                    <span>{title}</span>
                    <IoMdArrowDropdown className={`general-dropdown-arrow ${open ? "open" : ""}`} />
                </div>
                <div className={`general-dropdown-options ${open ? "open" : ""}`}>
                    {options.map((o) => (
                        <div
                            key={o}
                            className={`general-dropdown-option ${selected === o ? "active" : ""}`}
                            onClick={() => onSelect(o)}
                        >
                            {o}
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const [pipelineDropdownOpen, setPipelineDropdownOpen] = useState({
        area: false,
        status: false,
    });

    const [emailDropdownOpen, setEmailDropdownOpen] = useState({
        area: false,
        customer: false,
        status: false,
    });

    const [keyDropdownOpen, setKeyDropdownOpen] = useState({
        area: false,
        customer: false,
        active: false,
    });

    // Adding functionality to close dropdown whenever the user clicks outside its component
    useEffect(() => {
        function handleClickOutside(e: MouseEvent) {
            const target = e.target as HTMLElement;

            // If click is inside any dropdown area, do nothing
            if (
                target.closest(".general-sort-options") ||
                target.closest(".general-sort-dropdown") ||
                target.closest(".general-dropdown") ||
                target.closest(".general-dropdown-options")
            ) {
                return;
            }

            // Close ALL dropdowns
            setPipelineDropdownOpen({ area: false, status: false });
            setEmailDropdownOpen({ area: false, customer: false, status: false });
            setKeyDropdownOpen({ area: false, customer: false, active: false });

            // Also close <details> elements (Filter / Sort)
            document.querySelectorAll(".general-sort-dropdown").forEach((el) => {
                (el as HTMLDetailsElement).open = false;
            });
        }

        document.addEventListener("click", handleClickOutside);
        return () => document.removeEventListener("click", handleClickOutside);
    }, []);

    /* ----------------------------------------------------
        Handling opening and closing of slips
    ---------------------------------------------------- */
    // ai-slip open/close states — keys: "pipeline-<id>", "email-<id>", "key-<id>"
    const [slipState, setSlipState] = useState<{ [key: string]: boolean }>({});

    // Initialize slips as open when data loads
    useEffect(() => {
        setSlipState(prev => {
            const newState = { ...prev }; // Preserve existing state

            // Only add NEW entries that don't exist yet
            pipelineHistory.forEach(p => {
                const key = `pipeline-${p.entry_id}`;
                if (!(key in newState)) {
                    newState[key] = true;
                }
            });

            emailHistory.forEach(e => {
                const key = `email-${e.entry_id}`;
                if (!(key in newState)) {
                    newState[key] = true;
                }
            });

            keyHistory.forEach(k => {
                const key = `key-${k.entry_id}`;
                if (!(key in newState)) {
                    newState[key] = true;
                }
            });

            return newState;
        });
    }, [pipelineHistory, emailHistory, keyHistory]);

    const toggleSlip = (id: string) => setSlipState(prev => ({ ...prev, [id]: !prev[id] }));

    const expandAll = () => setSlipState(prev => Object.fromEntries(Object.keys(prev).map(k => [k, true])));

    const closeAll = () => setSlipState(prev => Object.fromEntries(Object.keys(prev).map(k => [k, false])));

    /* ----------------------------------------------------
        Handling ACTIONS
    ---------------------------------------------------- */

    const exportToCSV = (filename: string, rows: any[]) => {
        if (!rows || rows.length === 0) {
            alert("No data available for export.");
            return;
        }

        // Extract headers dynamically
        const headers = Object.keys(rows[0]);

        // Escape values properly
        const escape = (value: any) => {
            if (value === null || value === undefined) return "";
            const v = value.toString().replace(/"/g, '""');
            return `"${v}"`;
        };

        const csvContent = [
            headers.join(","),                                   // header line
            ...rows.map(row => headers.map(h => escape(row[h])).join(",")) // rows
        ].join("\n");

        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = filename;
        link.click();
    };

    /* ----------------------------------------------------
        PUTing to Prefect URL
    ---------------------------------------------------- */

    const [editingPipeline, setEditingPipeline] = useState<number | null>(null);
    const [newPrefectUrl, setNewPrefectUrl] = useState<string>("");


    const fetchCsrfToken = async () => {
        const response = await fetch('/api/csrf-token/', {
            method: 'GET',
            credentials: "same-origin", // include cookies in the request
        });

        if (response.ok) {
            const data = await response.json();
            return data.csrfToken; // Return the CSRF token
        } else {
            console.error('Failed to fetch CSRF token');
            return null;
        }
    };

    const updatePrefectUrl = async (p: PrefectPipelineData, newUrl: string) => {
        const csrfToken = await fetchCsrfToken();
        if (!csrfToken) return;

        // Build the full payload
        const body = {
            entry_id: p.entry_id,
            area: p.area,
            timestamp: p.timestamp,
            status: p.status,
            runtime: p.runtime,
            prefect_url: newUrl,
        };

        try {
            const response = await fetch(
                `/api/aiteam/pipeline/put/${p.entry_id}/`,
                {
                    method: "PUT",
                    headers: {
                        "Content-Type": "application/json",
                        "X-CSRFToken": csrfToken,
                    },
                    body: JSON.stringify(body),
                    credentials: "same-origin",
                }
            );

            if (!response.ok) {
                displayNotification(false, "Must be in a URL format. E.g. https://fireneuralnetwork.com/");
                return false;
            }

            const updatedPipeline = await response.json();
            displayNotification(true, "Prefect URL updated successfully.");
            setEditingPipeline(null);
            return updatedPipeline;
        } catch (error) {
            console.error("Error updating Prefect URL:", error);
            return false;
        }
    };

    /* ----------------------------------------------------
        HELPER FUNCTIONS
    ---------------------------------------------------- */

    function displayNotification(successOrFail: boolean, message: string) {
        if (successOrFail) {
            toast.success(message, {
                autoClose: 4000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                style: {
                    backgroundColor: '#e7ecf1ff',
                    color: 'black',
                    fontSize: '16px',
                }
            });
        }
        else {
            toast.error(message, {
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                style: {
                    backgroundColor: '#e7ecf1ff',
                    color: 'black',
                    fontSize: '16px',
                }
            });
        }
    }

    // Formats UTC timestamp into "MM/DD/YYYY, HH:MM:SS AM/PM" but stays in UTC
    const formatUtc = (ts: string) => {
        const date = new Date(ts);
        return new Intl.DateTimeFormat("en-US", {
            month: "numeric",
            day: "numeric",
            year: "numeric",
            hour: "numeric",
            minute: "numeric",
            second: "numeric",
            hour12: true,
            timeZone: "UTC",  // <-- keeps it in UTC
        }).format(date);
    };


    /* ----------------------------------------------------
        RENDERING
    ---------------------------------------------------- */

    return (
        <div className="customer-directory-body">
            <TeamHeader title="AI" />
            <ToastContainer />

            <div className="customer-title-and-expand-buttons">
                <h1 className="cutomer-title">Data & Access Management</h1>

                <div className="ai-expand-buttons">
                    <button className="general-sort-summary" onClick={expandAll}>Expand All</button>
                    <button className="general-sort-summary" onClick={closeAll}>Close All</button>

                </div>

            </div>

            <div className="ai-blocks">

                {/* ========================================================
                    PIPELINE BLOCK
                ========================================================= */}
                <div className="ai-block">
                    <div className="ai-action-buttons">
                        <div className="ai-action-buttons-g2">
                            <div className="general-action-buttons-g2">

                                {/* ---------------- FILTER ---------------- */}
                                <details className="general-sort-dropdown">
                                    <summary className="general-sort-summary">
                                        <IoOptionsOutline style={{ marginRight: "0.25rem", fontSize: "1rem" }} />
                                        <span>Filter</span>
                                    </summary>
                                    <div className="general-sort-options">
                                        <Dropdown
                                            title="Area"
                                            options={Array.from(new Set(pipelineHistory.map((p) => p.area)))}
                                            selected={pipelineFilter.area}
                                            onSelect={(area) => toggleFilter(setPipelineFilter, "area", area)}
                                            open={pipelineDropdownOpen.area}
                                            setOpen={(val) => setPipelineDropdownOpen(prev => ({ ...prev, area: val }))}
                                        />

                                        <Dropdown
                                            title="Status"
                                            options={["Successful", "Failed"]}
                                            selected={pipelineFilter.status}
                                            onSelect={(status) => toggleFilter(setPipelineFilter, "status", status)}
                                            open={pipelineDropdownOpen.status}
                                            setOpen={(val) => setPipelineDropdownOpen(prev => ({ ...prev, status: val }))}
                                        />


                                        <button
                                            onClick={() => {
                                                setPipelineFilter({});
                                            }}
                                        >
                                            Reset
                                        </button>
                                    </div>
                                </details>

                                {/* ---------------- SORT ---------------- */}
                                <details className="general-sort-dropdown">
                                    <summary className="general-sort-summary">
                                        <BiSortAlt2 style={{ marginRight: "0.25rem", fontSize: "1rem" }} />
                                        <span>Sort</span>
                                    </summary>
                                    <div className="general-sort-options">

                                        <div
                                            className={`general-sort-option ${pipelineSort === "alphabetical" ? "active" : ""}`}
                                            onClick={() => setPipelineSort("alphabetical")}
                                        >
                                            Alphabetical
                                        </div>

                                        <div
                                            className={`general-sort-option ${pipelineSort === "last" ? "active" : ""}`}
                                            onClick={() => setPipelineSort("last")}
                                        >
                                            Timestamp
                                        </div>

                                        <div
                                            className={`general-sort-option ${pipelineSort === "runtime" ? "active" : ""}`}
                                            onClick={() => setPipelineSort("runtime")}
                                        >
                                            Runtime
                                        </div>


                                    </div>
                                </details>

                            </div>
                        </div>

                        {/* Actions */}
                        <details className="general-sort-dropdown">
                            <summary className="general-sort-summary">
                                <PiGearSix style={{ marginRight: "0.25rem", fontSize: "1rem" }} />
                                <span>Actions</span>
                            </summary>

                            <div className="general-sort-options">
                                <div
                                    className="general-sort-option"
                                    onClick={() => exportToCSV("pipeline.csv", sortedPipeline)}
                                >
                                    Export CSV
                                </div>

                            </div>
                        </details>
                    </div>

                    {/* -------- DATA LIST -------- */}
                    <h2 className="ai-block-title">Pipeline</h2>
                    <div className="ai-slips">
                        {sortedPipeline.map((p) => {
                            const id = `pipeline-${p.entry_id}`;
                            const open = slipState[id];

                            return (
                                <div
                                    className={`ai-slip ${!open ? "ai-slip-closed-hover" : ""}`}
                                    key={id}
                                    onClick={!open ? () => toggleSlip(id) : undefined}
                                >
                                    <div className="ai-slip-inner-title-and-buttons">
                                        <h1>{p.area}</h1>

                                        <div className="ai-slip-inner-buttons">
                                            <button onClick={() => toggleSlip(id)}>
                                                {open ? <IoMdArrowDropup /> : <IoMdArrowDropdown onClick={() => toggleSlip(id)} />}
                                            </button>
                                        </div>
                                    </div>

                                    {open && (
                                        <div className="ai-slip-info">
                                            <div>
                                                <strong>Timestamp</strong>
                                                <p>{formatUtc(p.timestamp)}</p>
                                            </div>
                                            <div>
                                                <strong>Status</strong>
                                                <p style={{ color: getStatusColor(p.status, "status") }}>
                                                    {p.status ? "Successful" : "Failed"}
                                                </p>
                                            </div>
                                            <div>
                                                <strong>Runtime</strong>
                                                <p>{p.runtime}</p>
                                            </div>
                                            <div>
                                                <span style={{ display: 'flex', gap: '0.3rem', alignItems: 'start' }}>
                                                    <strong>Prefect URL</strong>
                                                    {editingPipeline !== p.entry_id && (
                                                        <button
                                                            onClick={() => {
                                                                setEditingPipeline(p.entry_id);
                                                                setNewPrefectUrl(p.prefect_url || "");
                                                            }}
                                                        >
                                                            <HiPencilSquare />
                                                        </button>
                                                    )}
                                                </span>

                                                {editingPipeline === p.entry_id ? (
                                                    <input
                                                        type="text"
                                                        value={newPrefectUrl}
                                                        autoFocus
                                                        onChange={(e) => setNewPrefectUrl(e.target.value)}
                                                        onKeyDown={async (e) => {
                                                            if (e.key === "Enter") {
                                                                const result = await updatePrefectUrl(p, newPrefectUrl);
                                                                if (result) {
                                                                    // Update local state immediately
                                                                    p.prefect_url = newPrefectUrl;
                                                                }
                                                            } else if (e.key === "Escape") {
                                                                setEditingPipeline(null);
                                                            }
                                                        }}

                                                        onBlur={() => setEditingPipeline(null)} // cancel on blur
                                                        style={{ width: "100%" }}
                                                    />
                                                ) : (
                                                    <a className="ai-slip-url general-link" title={p.prefect_url} href={p.prefect_url} target="_blank" rel="noreferrer">{shortUrl(p.prefect_url)}</a>                                             
                                                )}
                                            </div>

                                        </div>
                                    )}
                                </div>
                            );
                        })}

                    </div>
                </div>

                {/* ========================================================
                    EMAIL BLOCK
                ========================================================= */}
                <div className="ai-block">

                    <div className="ai-action-buttons">
                        <div className="ai-action-buttons-g2">
                            <div className="general-action-buttons-g2">

                                {/* ---------------- FILTER ---------------- */}
                                <details className="general-sort-dropdown">
                                    <summary className="general-sort-summary">
                                        <IoOptionsOutline style={{ marginRight: "0.25rem", fontSize: "1rem" }} />
                                        <span>Filter</span>
                                    </summary>
                                    <div className="general-sort-options">

                                        <Dropdown
                                            title="Area"
                                            options={Array.from(new Set(emailHistory.map((e) => e.area)))}
                                            selected={emailFilter.area}
                                            onSelect={(area) => toggleFilter(setEmailFilter, "area", area)}
                                            open={emailDropdownOpen.area}
                                            setOpen={(val) => setEmailDropdownOpen(prev => ({ ...prev, area: val }))}
                                        />

                                        <Dropdown
                                            title="Customer"
                                            options={orgs.map((o) => o.ORG_NAME)} // Use org names for options
                                            selected={emailFilter.customer}      // Selected customer
                                            onSelect={(customer) => toggleFilter(setEmailFilter, "customer", customer)} // Update filter
                                            open={emailDropdownOpen.customer}    // Dropdown open state
                                            setOpen={(val) => setEmailDropdownOpen(prev => ({ ...prev, customer: val }))} // Update open state
                                        />


                                        <Dropdown
                                            title="Status"
                                            options={["Successful", "Failed"]}
                                            selected={emailFilter.status}
                                            onSelect={(status) => toggleFilter(setEmailFilter, "status", status)}
                                            open={emailDropdownOpen.status}
                                            setOpen={(val) => setEmailDropdownOpen(prev => ({ ...prev, status: val }))}
                                        />

                                        <button
                                            onClick={() => {
                                                setEmailFilter({});
                                            }}
                                        >
                                            Reset
                                        </button>
                                    </div>
                                </details>

                                {/* ---------------- SORT ---------------- */}
                                <details className="general-sort-dropdown">
                                    <summary className="general-sort-summary">
                                        <BiSortAlt2 style={{ marginRight: "0.25rem", fontSize: "1rem" }} />
                                        <span>Sort</span>
                                    </summary>
                                    <div className="general-sort-options">

                                        <div
                                            className={`general-sort-option ${emailSort === "alphabetical" ? "active" : ""}`}
                                            onClick={() => setEmailSort("alphabetical")}
                                        >
                                            Alphabetical
                                        </div>

                                        <div
                                            className={`general-sort-option ${emailSort === "last" ? "active" : ""}`}
                                            onClick={() => setEmailSort("last")}
                                        >
                                            Timestamp
                                        </div>


                                    </div>
                                </details>

                            </div>
                        </div>

                        {/* Actions */}
                        <details className="general-sort-dropdown">
                            <summary className="general-sort-summary">
                                <PiGearSix style={{ marginRight: "0.25rem", fontSize: "1rem" }} />
                                <span>Actions</span>
                            </summary>

                            <div className="general-sort-options">
                                <div
                                    className="general-sort-option"
                                    onClick={() => exportToCSV("email.csv", sortedEmail)}
                                >
                                    Export CSV
                                </div>

                            </div>
                        </details>
                    </div>

                    {/* -------- DATA LIST -------- */}
                    <h2 className="ai-block-title">Email</h2>
                    <div className="ai-slips">
                        {sortedEmail.map((e) => {
                            const id = `email-${e.entry_id}`;
                            const open = slipState[id];

                            return (
                                <div
                                    className={`ai-slip ${!open ? "ai-slip-closed-hover" : ""}`}
                                    key={id}
                                    onClick={!open ? () => toggleSlip(id) : undefined}
                                >
                                    <div className="ai-slip-inner-title-and-buttons">
                                        <h1>
                                            {e.area} |{" "}
                                            {orgs.find((o) => o.id === e.organization)?.ORG_NAME ||
                                                e.organization}
                                        </h1>

                                        <div className="ai-slip-inner-buttons">
                                            <button onClick={() => toggleSlip(id)}>
                                                {open ? <IoMdArrowDropup /> : <IoMdArrowDropdown onClick={() => toggleSlip(id)} />}
                                            </button>
                                        </div>
                                    </div>

                                    {open && (
                                        <div className="ai-slip-info">
                                            <div>
                                                <strong>Timestamp</strong>
                                                <p>{formatUtc(e.email_timestamp)}</p>
                                            </div>

                                            <div>
                                                <strong>Status</strong>
                                                <p style={{ color: getStatusColor(e.status, "status") }}>
                                                    {e.status ? "Successful" : "Failed"}
                                                </p>
                                            </div>

                                            <div>
                                                <strong>Empty Email?</strong>
                                                <p>{e.empty_email ? "Yes" : "No"}</p>
                                            </div>

                                            <div>
                                                <strong>Content</strong>
                                                <p>{e.content}</p>
                                            </div>
                                        </div>

                                    )}
                                </div>
                            );
                        })}

                    </div>
                </div>

                {/* ========================================================
                    KEY BLOCK
                ========================================================= */}
                <div className="ai-block">

                    <div className="ai-action-buttons">
                        <div className="ai-action-buttons-g2">
                            <div className="general-action-buttons-g2">

                                {/* ---------------- FILTER ---------------- */}
                                <details className="general-sort-dropdown">
                                    <summary className="general-sort-summary">
                                        <IoOptionsOutline style={{ marginRight: "0.25rem", fontSize: "1rem" }} />
                                        <span>Filter</span>
                                    </summary>
                                    <div className="general-sort-options">

                                        <Dropdown
                                            title="Area"
                                            options={Array.from(new Set(keyHistory.map((k) => k.area)))}
                                            selected={keyFilter.area}
                                            onSelect={(area) => toggleFilter(setKeyFilter, "area", area)}
                                            open={keyDropdownOpen.area}
                                            setOpen={(val) => setKeyDropdownOpen(prev => ({ ...prev, area: val }))}
                                        />

                                        <Dropdown
                                            title="Customer"
                                            options={orgs.map((o) => o.ORG_NAME)} // Use org names for options
                                            selected={keyFilter.customer}      // Selected customer
                                            onSelect={(customer) => toggleFilter(setKeyFilter, "customer", customer)} // Update filter
                                            open={keyDropdownOpen.customer}    // Dropdown open state
                                            setOpen={(val) => setKeyDropdownOpen(prev => ({ ...prev, customer: val }))} // Update open state
                                        />


                                        <Dropdown
                                            title="Active"
                                            options={["Yes", "No"]}
                                            selected={keyFilter.active}
                                            onSelect={(active) => toggleFilter(setKeyFilter, "active", active)}
                                            open={keyDropdownOpen.active}
                                            setOpen={(val) => setKeyDropdownOpen(prev => ({ ...prev, active: val }))}
                                        />

                                        <button
                                            onClick={() => {
                                                setKeyFilter({});
                                            }}
                                        >
                                            Reset
                                        </button>
                                    </div>
                                </details>

                                {/* ---------------- SORT ---------------- */}
                                <details className="general-sort-dropdown">
                                    <summary className="general-sort-summary">
                                        <BiSortAlt2 style={{ marginRight: "0.25rem", fontSize: "1rem" }} />
                                        <span>Sort</span>
                                    </summary>
                                    <div className="general-sort-options">

                                        <div
                                            className={`general-sort-option ${keySort === "alphabetical" ? "active" : ""}`}
                                            onClick={() => setKeySort("alphabetical")}
                                        >
                                            Alphabetical
                                        </div>

                                        <div
                                            className={`general-sort-option ${keySort === "last" ? "active" : ""}`}
                                            onClick={() => setKeySort("last")}
                                        >
                                            Last Used
                                        </div>

                                        <div
                                            className={`general-sort-option ${keySort === "active_since" ? "active" : ""}`}
                                            onClick={() => setKeySort("active_since")}
                                        >
                                            Active Since
                                        </div>

                                    </div>
                                </details>

                            </div>
                        </div>

                        {/* Actions */}
                        <details className="general-sort-dropdown">
                            <summary className="general-sort-summary">
                                <PiGearSix style={{ marginRight: "0.25rem", fontSize: "1rem" }} />
                                <span>Actions</span>
                            </summary>

                            <div className="general-sort-options">
                                <div
                                    className="general-sort-option"
                                    onClick={() => exportToCSV("keys.csv", sortedKey)}
                                >
                                    Export CSV
                                </div>

                            </div>
                        </details>
                    </div>

                    {/* -------- DATA LIST -------- */}

                    <h2 className="ai-block-title">Key</h2>
                    <div className="ai-slips">
                        {sortedKey.map((k) => {
                            const id = `key-${k.entry_id}`;
                            const open = slipState[id];

                            return (
                                <div
                                    className={`ai-slip ${!open ? "ai-slip-closed-hover" : ""}`}
                                    key={id}
                                    onClick={!open ? () => toggleSlip(id) : undefined}
                                >

                                    <div className="ai-slip-inner-title-and-buttons">
                                        <h1>
                                            {k.area} |{" "}
                                            {orgs.find((o) => o.id === k.organization)?.ORG_NAME ||
                                                k.organization}
                                        </h1>

                                        <div className="ai-slip-inner-buttons">
                                            <button onClick={() => toggleSlip(id)}>
                                                {open ? <IoMdArrowDropup /> : <IoMdArrowDropdown onClick={() => toggleSlip(id)} />}
                                            </button>
                                        </div>
                                    </div>

                                    {open && (
                                        <div className="ai-slip-info">
                                            <div>
                                                <strong>Last Used</strong>
                                                <p>{formatUtc(k.last_used)}</p>
                                            </div>

                                            <div>
                                                <strong>Active</strong>
                                                <p style={{ color: getStatusColor(k.active ? "Yes" : "No", "active") }}>
                                                    {k.active ? "Yes" : "No"}
                                                </p>
                                            </div>

                                            <div>
                                                <strong>Active Since</strong>
                                                <p>{k.active_since}</p>
                                            </div>

                                            <div>
                                                <strong>Header Key</strong>
                                                <p>{k.header_key ? "Yes" : "No"}</p>
                                            </div>
                                        </div>

                                    )}
                                </div>
                            );
                        })}

                    </div>

                </div>
            </div>
        </div>
    );
};

export default DataManagement;
